import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TrendingUp, Crown } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface ProfileCompletionBarProps {
  listing: any;
  listingId: string;
}

export const ProfileCompletionBar = ({ listing, listingId }: ProfileCompletionBarProps) => {
  const navigate = useNavigate();

  // Calculate completion percentage
  const completionFactors = [
    { name: "Información básica", completed: !!(listing.description && listing.address) },
    { name: "Contacto completo", completed: !!(listing.phone && listing.email && listing.website) },
    { name: "Imágenes (3+)", completed: !!(listing.images && listing.images.length >= 3) },
    { name: "Horarios", completed: !!(listing.opening_hours && Object.keys(listing.opening_hours).length > 0) },
    { name: "Redes sociales", completed: !!(listing.social_media && Object.keys(listing.social_media).length > 0) },
    { name: "Videos", completed: !!(listing.video_urls && listing.video_urls.length > 0) },
    { name: "Amenidades", completed: !!(listing.amenities && listing.amenities.length >= 3) },
    { name: "Certificaciones", completed: !!(listing.certifications && listing.certifications.length > 0) },
  ];

  const completedCount = completionFactors.filter(f => f.completed).length;
  const completionPercentage = Math.round((completedCount / completionFactors.length) * 100);
  const visibilityBoost = Math.round(completionPercentage / 2); // Up to 50% boost

  if (completionPercentage >= 90) {
    return null; // Hide if almost complete
  }

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-secondary/5">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-full">
              <TrendingUp className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">Aumenta tu Visibilidad</h3>
              <p className="text-xs text-muted-foreground">
                Tu perfil está {completionPercentage}% completo
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-primary">+{visibilityBoost}%</div>
            <p className="text-xs text-muted-foreground">potencial</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="relative w-full bg-muted rounded-full h-3 mb-4 overflow-hidden">
          <div 
            className="bg-gradient-primary h-3 rounded-full transition-all duration-500 relative"
            style={{ width: `${completionPercentage}%` }}
          >
            <div className="absolute inset-0 bg-white/20 animate-pulse" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs font-semibold text-foreground/70">
              {completionPercentage}%
            </span>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <p className="text-xs text-muted-foreground">
            <strong>Completa tu perfil para:</strong>
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-center gap-2 text-xs">
              <div className="w-1.5 h-1.5 rounded-full bg-primary" />
              <span>Mayor visibilidad (+{visibilityBoost}%)</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-1.5 h-1.5 rounded-full bg-primary" />
              <span>Más credibilidad</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-1.5 h-1.5 rounded-full bg-primary" />
              <span>Mejor posicionamiento</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-1.5 h-1.5 rounded-full bg-primary" />
              <span>Más conversiones</span>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={() => navigate(`/listing/${listingId}/edit`)}
            className="flex-1"
            size="sm"
          >
            Completar Perfil
          </Button>
          {!listing.is_premium && (
            <Button
              onClick={() => navigate("/premium-upgrade")}
              variant="outline"
              size="sm"
              className="flex-1"
            >
              <Crown className="w-4 h-4 mr-1" />
              Premium
            </Button>
          )}
        </div>

        {/* Missing items hint */}
        {completionPercentage < 100 && (
          <div className="mt-3 p-2 bg-muted/50 rounded text-xs text-muted-foreground">
            <strong>Falta:</strong>{" "}
            {completionFactors
              .filter(f => !f.completed)
              .slice(0, 3)
              .map(f => f.name)
              .join(", ")}
            {completionFactors.filter(f => !f.completed).length > 3 && "..."}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
